/**
 * SedimentBedPhysicsPage.tsx — Bed-Type Physics & Array Placement
 *
 * Shows how the active session's diffuser/module/array design interacts with
 * three different reservoir bed conditions across multiple depth points.
 * Includes the reservoir layout SVG with explicit array positions and
 * a printable report.
 *
 * Bed types analysed:
 *   1. Fine silt / clay (pre-dredge Barcombe condition)
 *   2. Medium silt        (typical UK reservoir bed)
 *   3. Fine sand          (post-dredge / restored bed)
 *
 * Depth points: 2.0, 2.5, 3.0, 3.5, 4.4 m
 *
 * Physics calculated per (bed × depth):
 *   - Bubble diameter at bed (Fritz/Loubière, depth-corrected)
 *   - Plume bed velocity (Seol 2007 jet/plume scaling)
 *   - Bed shear stress      τ = ρ·C_D·v²
 *   - Shields parameter     θ = τ / ((ρ_s−ρ_w)·g·d₅₀)
 *   - Resuspension factor   f_resusp (Mehta 1986)
 *   - Erosion rate          Partheniades (1965) cohesive law
 *   - SOD enhancement       baseline × resuspension factor
 *   - O₂ penetration depth  Rasmussen & Jørgensen (1992)
 */

import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  Mountain, Printer, ArrowLeft, ChevronDown, ChevronRight,
  Info, AlertTriangle, CheckCircle2, MapPin, Layers, Sparkles, Zap, Save,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { savePersisted, loadPersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';
import { generateSedimentBedPDF, generatePreDredgingPDF, generatePostDredgingPDF } from '@/lib/reservoirPdf';
import { SEASONAL_PROFILES, getProfile, DEFAULT_PROFILE_ID } from '@/lib/seasonalProfiles';
import {
  specFromLegacy, bubbleDiameterMm, bubbleRiseVelMs, sotePerMetre,
  bedInfluenceRadiusM, surfaceRadiusM, plumeBedVelocityMs, bedShearPa,
  shieldsParameter, moduleBedCoverageAreaM2, describeShape, PLUME_SHAPE_LABEL,
  poresPerUnit, poresPerModule, qPerModuleNm3h, rhoW,
} from '@/lib/diffuserPhysics';
import { toast } from 'sonner';

// ─── Physics constants ────────────────────────────────────────────────────────
const G            = 9.81;
const P_ATM        = 101_325;
const RHO_AIR_STD  = 1.293;
const O2_MASS_FRAC = 0.2314;
const DO_SAT_20    = 9.09;
const FRAME_SIZE   = 2.0;
const C_D          = 0.01;     // bed drag coefficient (van Rijn 1984)
const E_0          = 1e-4;     // erosion rate constant kg/m²/s (Partheniades 1965)

// ─── Reservoir polygon (same as other pages) ──────────────────────────────────
const RESERVOIR_POLY: [number, number][] = [
  [85, 18], [95, 15], [112, 14], [128, 18], [142, 24],
  [155, 34], [163, 48], [168, 65], [170, 82],
  [169, 100], [166, 118], [168, 136],
  [170, 155], [169, 173], [167, 192],
  [162, 210], [156, 228], [148, 245],
  [138, 262], [127, 277], [116, 290],
  [108, 302], [102, 315], [98, 330],
  [94, 345], [91, 360], [89, 375],
  [88, 390], [87, 403], [87, 415], [85, 432],
  [82, 420], [79, 408], [76, 396], [73, 383],
  [70, 368], [67, 352], [64, 336], [60, 320],
  [56, 303], [51, 287], [47, 270],
  [42, 253], [39, 236], [37, 218],
  [35, 200], [34, 182], [35, 165],
  [38, 148], [38, 130], [36, 112],
  [37, 94], [40, 77], [46, 62],
  [52, 48], [60, 35], [72, 25],
];

// ─── Array Zone Definitions (explicit positions) ─────────────────────────────
interface ArrayZone {
  id: string;          // 'A', 'B', …
  label: string;
  type: 'deepest' | 'deep' | 'silt';
  cx: number; cy: number;
  rx: number; ry: number;
  depthMid: number;
}

const ARRAY_ZONES: ArrayZone[] = [
  // Deepest (4.0–4.8 m) — NE basin
  { id: 'A', label: 'Deepest basin (NE)',         type: 'deepest', cx: 150, cy: 90,  rx: 16, ry: 22, depthMid: 4.4 },
  // Medium-deep (3.0–4.0 m) — scattered patches
  { id: 'B', label: 'NW-central deep patch',      type: 'deep',    cx: 92,  cy: 88,  rx: 30, ry: 42, depthMid: 3.5 },
  { id: 'C', label: 'Central-west deep',          type: 'deep',    cx: 62,  cy: 195, rx: 16, ry: 28, depthMid: 3.3 },
  { id: 'D', label: 'SE central deep',            type: 'deep',    cx: 100, cy: 278, rx: 18, ry: 24, depthMid: 3.2 },
  { id: 'E', label: 'East-central deep',          type: 'deep',    cx: 152, cy: 170, rx: 12, ry: 18, depthMid: 3.1 },
  // Silt zone (2–3 m) — dominant pre-dredge area, sub-divided
  { id: 'F', label: 'Silt zone — north',          type: 'silt',    cx: 105, cy: 135, rx: 22, ry: 26, depthMid: 2.5 },
  { id: 'G', label: 'Silt zone — middle',         type: 'silt',    cx: 115, cy: 230, rx: 26, ry: 30, depthMid: 2.4 },
  { id: 'H', label: 'Silt zone — south',          type: 'silt',    cx: 92,  cy: 340, rx: 18, ry: 32, depthMid: 2.3 },
];

// ─── Bed type definitions ─────────────────────────────────────────────────────
export interface BedType {
  id: string;
  label: string;
  desc: string;
  d50_um: number;     // median particle size [μm]
  rho_s: number;      // particle density [kg/m³]
  theta_cr: number;   // critical Shields parameter
  porosity: number;   // bed porosity
  k_cohesive: number; // cohesive resistance multiplier (1.0 = non-cohesive)
  colour: string;
  note: string;
}

const BED_TYPES: BedType[] = [
  {
    id: 'clay',
    label: 'Clay (cohesive)',
    desc: 'Pure clay bed — kaolinite/illite dominated, highly cohesive',
    d50_um: 2,     rho_s: 2680,  theta_cr: 0.20,   porosity: 0.85, k_cohesive: 5.0,
    colour: '#6b3a1f',
    note: 'Pure clay (kaolinite/illite). Despite tiny particle size, cohesive forces dominate — critical shear stress ~1-2 Pa (Mehta 1986; Winterwerp 2004), much higher than would be predicted from grain size alone. Floc structure must rupture before erosion begins (Partheniades 1965). Once mobilised, particles stay in suspension for days; this is the worst-case for taste & odour propagation. Highest cation-exchange capacity → strong P/heavy-metal binding when aerobic, sudden release when anoxic.',
  },
  {
    id: 'fine_silt',
    label: 'Fine silt',
    desc: 'Cohesive silt, typical pre-dredge accumulation',
    d50_um: 15,    rho_s: 2500,  theta_cr: 0.10,   porosity: 0.78, k_cohesive: 2.5,
    colour: '#a85d3a',
    note: 'Very fine silt, sticky, high water content. High SOD due to slow O₂ diffusion. Cohesive forces require higher shear to break floc structure — therefore higher critical Shields, but once mobilised, particles stay in suspension for hours.',
  },
  {
    id: 'medium_silt',
    label: 'Medium silt',
    desc: 'Typical UK reservoir bed, moderate accumulation',
    d50_um: 50,    rho_s: 2600,  theta_cr: 0.06,   porosity: 0.62, k_cohesive: 1.3,
    colour: '#8c7048',
    note: 'Mixed organic/mineral silt. Easily resuspended at moderate bed shear. Settles back within minutes when shear drops.',
  },
  {
    id: 'fine_sand',
    label: 'Fine sand',
    desc: 'Post-dredge restored bed, original geological substrate',
    d50_um: 200,   rho_s: 2650,  theta_cr: 0.045,  porosity: 0.42, k_cohesive: 1.0,
    colour: '#c8a868',
    note: 'Clean, non-cohesive, low SOD. Standard Shields theory applies directly. Stable foundation — once dredged, will not re-silt unless catchment loading is addressed.',
  },
];

// ─── Physics functions ────────────────────────────────────────────────────────
function rho_w(T: number) {
  return 999.842 + 6.793952e-2*T - 9.095290e-3*T**2 + 1.001685e-4*T**3 - 1.120083e-6*T**4;
}
function mu_w(T: number)    { return 2.414e-5 * Math.pow(10, 247.8 / (T + 133.15)); }
function nu_w(T: number)    { return mu_w(T) / rho_w(T); }
function sigma_w(T: number) { return Math.max(0.04, 0.07564 - 1.376e-4 * T); }
function doSat(T: number)   { return 468 / (31.6 + T); }

function bubbleDiamMm(poreSizeUm: number, qNm3h: number, pores: number, T: number) {
  const d_p = poreSizeUm / 1e6;
  const d_st = Math.pow((6 * sigma_w(T) * d_p) / (rho_w(T) * G), 1/3);
  const q_p = (qNm3h / 3600) / Math.max(1, pores);
  return d_st * (1 + 0.9 * Math.pow(Math.max(q_p * 1e9, 0.001), 0.25)) * 1000;
}
function bubbleAtBed(d_b_mm: number, depthM: number, T: number) {
  // Ideal-gas expansion from bed to surface (reverse: bigger at surface, smaller at bed)
  // d_bed × (P_bed/P_atm)^(1/3) = d_surf  →  d_bed = d_surf × (P_atm/P_bed)^(1/3)
  // But d_b_mm IS the bed bubble (Fritz at orifice = at bed). So at bed = d_b_mm directly.
  return d_b_mm;
}
function bubbleAtSurface(d_b_mm: number, depthM: number, T: number) {
  const P_bed = P_ATM + rho_w(T) * G * depthM;
  return d_b_mm * Math.pow(P_bed / P_ATM, 1/3);
}
function riseVel(d_mm: number, T: number) {
  const v0 = d_mm <= 0.5 ? 0.13 + 0.16*d_mm
           : d_mm <= 3.0 ? 0.14 + 0.065*d_mm
           : d_mm <= 6.0 ? 0.335 + 0.02*(d_mm - 3) : 0.395;
  return v0 * Math.pow(nu_w(20) / nu_w(T), 0.7);
}
function sotePerM(d_mm: number) {
  return Math.max(1.5, Math.min(9.5, 10.5 * Math.exp(-0.32 * d_mm)));
}
function diffuserPores(diffType: string, diamMm: number): number {
  const r = diamMm / 2;
  if (diffType === 'membrane_disc_ptfe') return Math.round(0.40 * Math.PI * r * r);
  if (diffType === 'ceramic_dome')       return Math.round(0.10 * Math.PI * r * r);
  if (diffType === 'hdpe_porous_tube')   return Math.round(0.08 * diamMm * 500);
  return Math.round(0.35 * Math.PI * r * r);
}

// Seol (2007) bubble plume bed velocity at the impingement zone
function plumeBedVelocity(qModuleNm3h: number, H_eff: number, T: number) {
  const B_0 = G * (qModuleNm3h / 3600);   // buoyancy flux per module
  // U_plume = 1.46 × (B₀ × H)^(1/3) — Seol 2007 jet/plume scaling
  return 1.46 * Math.pow(Math.max(B_0 * H_eff, 1e-12), 1/3);
}

// Shields & resuspension calculations for a bed type at given conditions
interface BedPhysics {
  d_b_bed: number;          // bubble Ø at bed [mm]
  d_b_surf: number;         // bubble Ø at surface [mm]
  v_rise: number;           // rise velocity [m/s]
  H_eff: number;            // submergence [m]
  soteM: number;            // %/m
  U_plume_bed: number;      // induced bed velocity [m/s]
  tau_bed: number;          // bed shear stress [Pa]
  theta: number;            // Shields parameter
  theta_cr_eff: number;     // effective critical Shields (with cohesion)
  resusp_factor: number;    // Mehta 1986
  erosion_rate: number;     // kg/m²/h (Partheniades)
  sod_enhance: number;      // SOD enhancement factor
  o2_penetration: number;   // mm into sediment
  stable: boolean;          // θ < θ_cr_eff
}

function calcBedPhysics(
  bed: BedType,
  depthM: number,
  spec: ModSpec,
): BedPhysics {
  const T          = spec.waterTempC;
  const H_eff      = Math.max(0.3, depthM - spec.mountingHeightM);
  // Build type-aware DiffuserSpec via the new central library
  const dSpec      = specFromLegacy(spec);
  const d_b_bed    = bubbleDiameterMm(dSpec, T);
  const d_b_surf   = bubbleAtSurface(d_b_bed, H_eff, T);
  const v_rise     = bubbleRiseVelMs(d_b_bed, T);
  const soteM      = sotePerMetre(d_b_bed);

  // Plume bed velocity is TYPE-DEPENDENT:
  //   - Disc: U = 1.46 × (B0 × H)^(1/3)   [Seol 2007 - point source]
  //   - Tube: U = 1.00 × (B0/L)^(1/3)      [Wuest 1992 - line source]
  const U_plume_bed = plumeBedVelocityMs(dSpec, H_eff);
  const tau_bed     = bedShearPa(dSpec, H_eff, T);
  const theta       = shieldsParameter(dSpec, H_eff, T, { d50_um: bed.d50_um, rho_s_kgm3: bed.rho_s });
  const theta_cr_eff = bed.theta_cr * bed.k_cohesive;
  const stable      = theta < theta_cr_eff;
  const resusp_factor = stable ? 1.0 : Math.min(3.0, 1 + 0.5 * (theta / theta_cr_eff - 1));
  const erosion_rate  = stable ? 0 : E_0 * (theta / theta_cr_eff - 1) * 3600;
  const sod_enhance   = resusp_factor;
  // O₂ penetration depth in sediment: clay << silt << sand
  // Reference: Rasmussen & Jørgensen 1992; Cai & Sayles 1996
  const o2_penetration =
    bed.id === 'fine_sand'   ? 8.0 :
    bed.id === 'medium_silt' ? 3.5 :
    bed.id === 'fine_silt'   ? 1.2 :
    bed.id === 'clay'        ? 0.4 :   // very thin aerobic layer in cohesive clay
                               1.2;

  // Suppress unused legacy import warnings (kept for backward compat)
  void rhoW; void poresPerUnit; void poresPerModule; void qPerModuleNm3h;
  void surfaceRadiusM; void bedInfluenceRadiusM; void moduleBedCoverageAreaM2;
  void describeShape; void PLUME_SHAPE_LABEL;

  return {
    d_b_bed, d_b_surf, v_rise, H_eff, soteM,
    U_plume_bed, tau_bed, theta, theta_cr_eff,
    resusp_factor, erosion_rate, sod_enhance, o2_penetration, stable,
  };
}

// ─── Array placement (positions) ──────────────────────────────────────────────
interface ArrayPlacement {
  zone: ArrayZone;
  moduleCount: number;
  modules: { x: number; y: number }[];
  spacing: number;
}

function placeModulesInZone(zone: ArrayZone, count: number, minSpacing: number) {
  if (count <= 0) return { positions: [] as { x: number; y: number }[], spacing: 0 };
  // Pack in hex grid centred on zone; spacing scaled to fit
  const area = Math.PI * zone.rx * zone.ry;
  const spacingTry = Math.max(minSpacing, Math.sqrt(area / Math.max(1, count) / 0.85));
  const positions: { x: number; y: number }[] = [];
  // Generate hex grid centred on zone
  const maxR = Math.max(zone.rx, zone.ry);
  const stepX = spacingTry;
  const stepY = spacingTry * 0.866;
  const radius = Math.ceil(maxR / spacingTry) + 1;
  // ring expansion until we have enough
  for (let r = 0; r <= radius && positions.length < count; r++) {
    if (r === 0) {
      positions.push({ x: zone.cx, y: zone.cy });
      continue;
    }
    // Hexagonal ring r
    const ringPoints: { x: number; y: number }[] = [];
    for (let i = 0; i < 6 * r; i++) {
      const angle = (i / (6 * r)) * 2 * Math.PI;
      const px = zone.cx + r * stepX * Math.cos(angle);
      const py = zone.cy + r * stepY * Math.sin(angle);
      // Constrain to ellipse
      if (((px - zone.cx) / zone.rx) ** 2 + ((py - zone.cy) / zone.ry) ** 2 <= 1.05) {
        ringPoints.push({ x: px, y: py });
      }
    }
    for (const p of ringPoints) {
      if (positions.length >= count) break;
      positions.push(p);
    }
  }
  return { positions: positions.slice(0, count), spacing: spacingTry };
}

// ─── Module spec ──────────────────────────────────────────────────────────────
interface ModSpec {
  diffuserCount: number; cornerOffsetM: number; mountingHeightM: number;
  diffuserType: string;  diffuserDiamMm: number; poreSizeUm: number;
  airflowPerDiffNm3h: number; diffuserDropKpa: number; waterDepthM: number;
  waterTempC: number; currentDoMgL: number; alphaFactor: number;
  betaFactor: number; blowerEff: number; safetyPct: number;
  // Tube-specific (optional - used when diffuserType is *_porous_tube)
  tubeLengthM?: number;        // 0.5 - 2.0 m
  tubeCount?: number;          // 1 - 4
  tubeDiamMm?: number;         // 50 - 100 mm
  tubePattern?: 'perimeter' | 'parallel' | 'cross' | 'centre_radial';
  tubeCentreOffsetM?: number;  // 0 - 0.95 m
  porePerCm?: number;          // 6 - 12 pores per cm
}
const DEFAULTS: ModSpec = {
  diffuserCount: 8, cornerOffsetM: 0.35, mountingHeightM: 0.40,
  diffuserType: 'membrane_disc_epdm', diffuserDiamMm: 300,
  poreSizeUm: 80, airflowPerDiffNm3h: 1.2, diffuserDropKpa: 1.5,
  waterDepthM: 3.0, waterTempC: 12, currentDoMgL: 2.0,
  alphaFactor: 0.85, betaFactor: 0.98, blowerEff: 0.70, safetyPct: 10,
};
function loadSpec(): ModSpec {
  return loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS);
}

// ─── Recommendation engine ────────────────────────────────────────────────────
// Goal: minimise bed disturbance (θ/θ_cr_eff → < 1.0) for the WORST-CASE bed
// while maintaining adequate SOTE (bubble Ø in 2–3 mm fine-bubble band).
//
// Variables to optimise:
//   - mountingHeightM:  0.3–1.0 m (higher → less plume velocity at bed)
//   - airflowPerDiff:   0.5–2.0 Nm³/h (lower → smaller plume, smaller bubble)
//   - poreSizeUm:       40–200 μm (smaller → smaller bubble, higher SOTE)
//   - diffuserCount:    4–12 per module (more → distributed airflow per pore)

interface Recommendations {
  bedId: string;             // worst-case bed
  criticalDepthM: number;
  recMountingHeightM: number;
  recAirflowPerDiff: number;
  recPoreSizeUm: number;
  recDiffuserCount: number;
  expectedTheta: number;
  expectedRatio: number;     // θ / θ_cr_eff
  rationale: string;
}

function recommendSettings(
  beds: BedType[],
  depths: number[],
  currentSpec: ModSpec,
): Recommendations {
  // Identify worst-case: bed with lowest θ_cr_eff (easiest to disturb)
  const worst = beds.reduce((w, b) =>
    (b.theta_cr * b.k_cohesive) < (w.theta_cr * w.k_cohesive) ? b : w, beds[0]);
  // Worst-case depth = shallowest where modules are placed (most plume per H_eff)
  const minDepth = Math.min(...depths.filter(d => d >= 2.0));

  // Sweep parameters to find a combination yielding θ/θ_cr < 0.5 (safety margin)
  const heightOptions   = [0.30, 0.40, 0.50, 0.60, 0.70, 0.80];
  const airflowOptions  = [0.5, 0.8, 1.0, 1.2, 1.5, 1.8];
  const poreOptions     = [40, 60, 80, 100, 120, 150];
  const countOptions    = [4, 6, 8, 10, 12];

  let best: { spec: ModSpec; theta: number; ratio: number; score: number } | null = null;

  for (const h of heightOptions) {
    for (const q of airflowOptions) {
      for (const p of poreOptions) {
        for (const n of countOptions) {
          const trial: ModSpec = { ...currentSpec,
            mountingHeightM: h, airflowPerDiffNm3h: q, poreSizeUm: p, diffuserCount: n };
          const phys = calcBedPhysics(worst, minDepth, trial);
          const ratio = phys.theta / phys.theta_cr_eff;
          // Hard constraints
          if (phys.d_b_bed < 1.0 || phys.d_b_bed > 5.0) continue;  // bubble band
          if (phys.soteM < 2.5) continue;                            // min efficiency
          if (q < 0.5) continue;                                     // diffuser min flow
          // Score: prefer low ratio, decent SOTE, moderate height
          const score = ratio * 10                                   // stability (weight 10)
                      + (1 / Math.max(phys.soteM, 0.1)) * 2          // SOTE inverse (weight 2)
                      + (h - 0.4) * 0.5                              // prefer not too high
                      + (n - 8) * 0.1;                               // prefer 8 diffusers
          if (!best || score < best.score) {
            best = { spec: trial, theta: phys.theta, ratio, score };
          }
        }
      }
    }
  }

  // Fallback if no combination satisfies constraints
  if (!best) {
    const fallback: ModSpec = { ...currentSpec,
      mountingHeightM: 0.6, airflowPerDiffNm3h: 0.8, poreSizeUm: 60, diffuserCount: 8 };
    const phys = calcBedPhysics(worst, minDepth, fallback);
    best = { spec: fallback, theta: phys.theta, ratio: phys.theta / phys.theta_cr_eff, score: 0 };
  }

  const rationale =
    `Optimisation targets the worst-case bed (${worst.label}, θ_cr_eff = ${(worst.theta_cr * worst.k_cohesive).toFixed(3)}) ` +
    `at the shallowest deployment depth (${minDepth} m). ` +
    `Mounting height ${best.spec.mountingHeightM.toFixed(2)} m raises the diffuser frame off the silt surface to weaken plume impingement at the bed. ` +
    `Airflow ${best.spec.airflowPerDiffNm3h.toFixed(2)} Nm³/h per diffuser produces a ${calcBedPhysics(worst, minDepth, best.spec).d_b_bed.toFixed(2)} mm bubble — in the fine-bubble band — while keeping plume buoyancy flux moderate. ` +
    `Pore size ${best.spec.poreSizeUm} μm and ${best.spec.diffuserCount} diffusers per module distribute the air across maximum pore count to keep individual bubble sizes consistent and minimise coalescence. ` +
    `Expected Shields ratio θ/θ_cr_eff = ${best.ratio.toFixed(2)}× — ${best.ratio < 1 ? 'BELOW threshold (bed stable)' : best.ratio < 1.5 ? 'marginal, expect mild resuspension' : 'ABOVE threshold (bed will erode — review)'}.`;

  return {
    bedId: worst.id, criticalDepthM: minDepth,
    recMountingHeightM: best.spec.mountingHeightM,
    recAirflowPerDiff: best.spec.airflowPerDiffNm3h,
    recPoreSizeUm: best.spec.poreSizeUm,
    recDiffuserCount: best.spec.diffuserCount,
    expectedTheta: best.theta,
    expectedRatio: best.ratio,
    rationale,
  };
}
function fmt(v: number, dp = 3): string { return isNaN(v) || !isFinite(v) ? '—' : v.toFixed(dp); }
function fmtSci(v: number, dp = 2): string { return isNaN(v) || !isFinite(v) ? '—' : v.toExponential(dp); }

// ─── Expandable component ─────────────────────────────────────────────────────
function Expandable({ title, defaultOpen = false, children }:
  { title: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-700/40 print:border-slate-300 rounded-lg overflow-hidden mb-2">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left bg-slate-800/40 print:bg-slate-100 hover:bg-slate-800/60 transition-all">
        {open ? <ChevronDown className="w-3.5 h-3.5 text-rb-brand print:text-slate-700" />
              : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
        <span className="text-[11px] font-semibold text-slate-200 print:text-black">{title}</span>
      </button>
      {(open || typeof window !== 'undefined' && window.matchMedia('print').matches) && (
        <div className="p-3 text-[11px] text-slate-300 print:text-slate-700 leading-relaxed bg-slate-900/30 print:bg-white">
          {children}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
//                              MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
export default function SedimentBedPhysicsPage() {
  const navigate = useNavigate();
  const spec = useMemo(loadSpec, []);

  const [selectedBed, setSelectedBed] = useState<string>('fine_silt');
  const [selectedDepth, setSelectedDepth] = useState<number>(3.0);
  const [seasonId, setSeasonId] = useState<string>(() =>
    loadPersisted<{ id: string }>(KEYS.BED_RECOMMENDATIONS + '__season', { id: DEFAULT_PROFILE_ID }).id ?? DEFAULT_PROFILE_ID);
  const profile = getProfile(seasonId);
  useEffect(() => { savePersisted(KEYS.BED_RECOMMENDATIONS + '__season', { id: seasonId }); }, [seasonId]);

  const bed = BED_TYPES.find(b => b.id === selectedBed) ?? BED_TYPES[0];
  const DEPTHS = [2.0, 2.5, 3.0, 3.5, 4.4];

  // Compute physics matrix: 3 beds × 5 depths
  const matrix = useMemo(() => {
    const m: Record<string, Record<number, BedPhysics>> = {};
    for (const b of BED_TYPES) {
      m[b.id] = {};
      for (const d of DEPTHS) {
        m[b.id][d] = calcBedPhysics(b, d, spec);
      }
    }
    return m;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec]);

  // Compute array placements based on session (use a representative total module count)
  // Allocate modules to zones weighted by depth & area
  // Compute recommendations from active session
  const recommendations = useMemo(() => recommendSettings(BED_TYPES, DEPTHS, spec), [spec]);

  // Cross-tab sync: re-load when storage changes
  useEffect(() => {
    return onPersistChange((key) => {
      if (key === KEYS.DIFFUSER_MODULE) {
        window.location.reload();
      }
    });
  }, []);

  // Apply recommended settings to the active session
  const applyRecommendations = () => {
    const newSpec: ModSpec = {
      ...spec,
      mountingHeightM:    recommendations.recMountingHeightM,
      airflowPerDiffNm3h: recommendations.recAirflowPerDiff,
      poreSizeUm:         recommendations.recPoreSizeUm,
      diffuserCount:      recommendations.recDiffuserCount,
    };
    savePersisted(KEYS.DIFFUSER_MODULE, newSpec);
    savePersisted(KEYS.BED_RECOMMENDATIONS, recommendations);
    toast.success('Recommended settings applied to all pages');
    setTimeout(() => window.location.reload(), 600);
  };

  const placements = useMemo<ArrayPlacement[]>(() => {
    // For session-based allocation: total = roughly 16 modules (4 ha × 4/ha) as default
    const defaultTotal = 16;
    const weights = ARRAY_ZONES.map(z => ({
      ...z,
      area: Math.PI * z.rx * z.ry,
      weight: Math.PI * z.rx * z.ry * (1 + 0.3 * (z.depthMid - 2.0)),
    }));
    const totalWeight = weights.reduce((s, z) => s + z.weight, 0);
    const minSpacing = 8;
    return weights.map(z => {
      const count = Math.max(1, Math.round(defaultTotal * z.weight / totalWeight));
      const { positions, spacing } = placeModulesInZone(z, count, minSpacing);
      return { zone: z, moduleCount: positions.length, modules: positions, spacing };
    });
  }, []);

  const totals = {
    arrays: placements.length,
    modules: placements.reduce((s, p) => s + p.moduleCount, 0),
    diffusers: placements.reduce((s, p) => s + p.moduleCount * spec.diffuserCount, 0),
  };

  // Inject seasonal-profile values into a spec copy for PDF calls
  const seasonalSpec = {
    ...spec,
    waterTempC: profile.waterTempC,
    currentDoMgL: profile.backgroundDOMgL,
    alphaFactor: profile.alphaFactor,
    betaFactor: profile.betaFactor,
  };

  // PDF generation — bed physics
  const downloadBedPhysicsPDF = () => {
    generateSedimentBedPDF({
      spec: {
        diffuserCount: seasonalSpec.diffuserCount, diffuserType: seasonalSpec.diffuserType,
        diffuserDiamMm: seasonalSpec.diffuserDiamMm, poreSizeUm: seasonalSpec.poreSizeUm,
        airflowPerDiffNm3h: seasonalSpec.airflowPerDiffNm3h,
        mountingHeightM: seasonalSpec.mountingHeightM, waterTempC: seasonalSpec.waterTempC,
        alphaFactor: seasonalSpec.alphaFactor,
      },
      beds: BED_TYPES.map(b => ({
        id: b.id, label: b.label, desc: b.desc,
        d50_um: b.d50_um, rho_s: b.rho_s, theta_cr: b.theta_cr, k_cohesive: b.k_cohesive,
      })),
      depths: DEPTHS,
      matrix,
      arrays: placements.map(p => ({
        id: p.zone.id, zoneLabel: p.zone.label, depthMid: p.zone.depthMid,
        cx: p.zone.cx, cy: p.zone.cy, moduleCount: p.moduleCount, spacing: p.spacing,
      })),
      recommendations,
    }, profile);
    toast.success(`Bed Physics PDF downloaded (${profile.label})`);
  };

  // PDF generation — pre-dredging scenario
  const downloadPreDredgingPDF = () => {
    generatePreDredgingPDF({
      spec: seasonalSpec,
      reservoirAreaHa: 3.4,
      baseWaterDepthM: 3.5,
      arrays: placements.map(p => ({
        id: p.zone.id, zoneLabel: p.zone.label, depthMid: p.zone.depthMid,
        cx: p.zone.cx, cy: p.zone.cy, moduleCount: p.moduleCount,
      })),
    }, profile);
    toast.success(`Pre-Dredging Report downloaded (${profile.label})`);
  };

  // PDF generation — post-dredging scenario
  const downloadPostDredgingPDF = () => {
    generatePostDredgingPDF({
      spec: seasonalSpec,
      reservoirAreaHa: 3.4,
      baseWaterDepthM: 3.5,
      arrays: placements.map(p => ({
        id: p.zone.id, zoneLabel: p.zone.label, depthMid: p.zone.depthMid,
        cx: p.zone.cx, cy: p.zone.cy, moduleCount: p.moduleCount,
      })),
    }, profile);
    toast.success(`Post-Dredging Report downloaded (${profile.label})`);
  };

  return (
    <div className="flex flex-col h-full overflow-auto print:overflow-visible print:bg-white">
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          body { background: white !important; color: black !important; }
          .print\\:break-before-page { break-before: page; }
          .print\\:break-inside-avoid { break-inside: avoid; }
        }
      `}</style>

      <div className="print:hidden">
        <PageHeader
          title="Sediment Bed Physics"
          subtitle="Active-session interaction analysis — 3 bed types × 5 depth points + array placement"
          icon={Mountain}
          actions={
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={downloadPreDredgingPDF}
                title="Generate Pre-Dredging scenario PDF report"
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25 transition-all font-semibold">
                <Printer className="w-3.5 h-3.5" /> Pre-Dredge PDF
              </button>
              <button onClick={downloadPostDredgingPDF}
                title="Generate Post-Dredging scenario PDF report"
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 transition-all font-semibold">
                <Printer className="w-3.5 h-3.5" /> Post-Dredge PDF
              </button>
              <button onClick={downloadBedPhysicsPDF}
                title="Generate Bed Physics & Recommendations PDF"
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-rb-brand/15 text-rb-brand border border-rb-brand/30 hover:bg-rb-brand/25 transition-all font-semibold">
                <Printer className="w-3.5 h-3.5" /> Bed Physics PDF
              </button>
              <button onClick={() => navigate('/reservoir-layout')}
                className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" /> Layout
              </button>
            </div>
          }
        />
      </div>

      <div className="flex-1 overflow-auto p-4 max-w-6xl mx-auto w-full print:p-0 print:max-w-none">

        {/* ── Print header (only visible in print) ───────────────────────── */}
        <div className="hidden print:block mb-4 pb-2 border-b-2 border-slate-700">
          <div className="text-[10px] tracking-[0.3em] font-bold uppercase text-slate-600">Reynolds &amp; Bauhm Smart Water</div>
          <div className="text-lg font-black">Sediment Bed Physics — Barcombe Reservoir Aeration</div>
          <div className="text-[10px] text-slate-600 mt-0.5">Active-session interaction analysis · Generated {new Date().toLocaleString('en-GB')}</div>
        </div>

        {/* ── Active session info ────────────────────────────────────────── */}
        <div className="rb-card p-3 mb-4 bg-emerald-500/5 border-emerald-500/30 print:bg-slate-50 print:border-slate-400 print:break-inside-avoid">
          <div className="flex items-start gap-2.5">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 print:text-black shrink-0 mt-0.5" />
            <div className="text-[11px] text-slate-300 print:text-black flex-1">
              <div className="font-semibold text-emerald-300 print:text-black mb-1">Active session — diffuser/module design</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
                <div><span className="text-slate-500 print:text-slate-600">Type:</span> <strong>{spec.diffuserType}</strong></div>
                <div><span className="text-slate-500 print:text-slate-600">Diff/module:</span> <strong>{spec.diffuserCount}</strong></div>
                <div><span className="text-slate-500 print:text-slate-600">Diam:</span> <strong>{spec.diffuserDiamMm} mm</strong></div>
                <div><span className="text-slate-500 print:text-slate-600">Pore:</span> <strong>{spec.poreSizeUm} μm</strong></div>
                <div><span className="text-slate-500 print:text-slate-600">Q/diff:</span> <strong>{fmt(spec.airflowPerDiffNm3h, 2)} Nm³/h</strong></div>
                <div><span className="text-slate-500 print:text-slate-600">Mount h:</span> <strong>{fmt(spec.mountingHeightM, 2)} m</strong></div>
                <div><span className="text-slate-500 print:text-slate-600">Water T:</span> <strong>{spec.waterTempC} °C</strong></div>
                <div><span className="text-slate-500 print:text-slate-600">α:</span> <strong>{fmt(spec.alphaFactor, 2)}</strong></div>
              </div>
            </div>
          </div>
        </div>

        {/* ─── SEASONAL PROFILE SELECTOR ────────────────────────────────── */}
        <div className="rb-card p-4 mb-4 print:break-inside-avoid">
          <h2 className="text-sm font-bold text-slate-100 print:text-black mb-2 flex items-center gap-2">
            <Info className="w-4 h-4 text-rb-brand print:text-black" />
            Seasonal Water Profile (used in ALL calculations &amp; reports)
          </h2>
          <p className="text-[11px] text-slate-400 print:text-slate-700 mb-3">
            Selecting a profile cascades through every calculation. Water temperature, baseline DO, SOD,
            alpha and beta factors all update. The selected profile is recorded on every PDF report.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-3">
            {SEASONAL_PROFILES.map(p => (
              <button key={p.id} onClick={() => setSeasonId(p.id)}
                className={`text-left rounded-lg p-2.5 border-2 transition-all ${
                  seasonId === p.id
                    ? 'bg-rb-brand/15 border-rb-brand/40 print:bg-slate-100'
                    : 'border-slate-700/40 hover:border-slate-600 bg-slate-800/20'
                }`}>
                <div className="text-[10px] font-bold text-slate-100 print:text-black mb-0.5">{p.label}</div>
                <div className="text-[9px] text-slate-400 grid grid-cols-2 gap-x-2 leading-tight">
                  <div>T:</div><div className="text-slate-200 font-mono">{p.waterTempC}°C</div>
                  <div>DO₀:</div><div className="text-slate-200 font-mono">{p.backgroundDOMgL} mg/L</div>
                  <div>SOD:</div><div className="text-slate-200 font-mono">{p.sodRateG_m2_d}</div>
                  <div>α:</div><div className="text-slate-200 font-mono">{p.alphaFactor}</div>
                </div>
              </button>
            ))}
          </div>
          <div className="p-3 rounded-xl bg-slate-800/40 border border-slate-700/40 text-[10px] text-slate-300 print:bg-white print:border-slate-300">
            <strong className="text-rb-brand print:text-black">{profile.label}</strong> — {profile.desc}<br />
            <strong>Months:</strong> {profile.monthsRange} ·{' '}
            <strong>Algal risk:</strong> {profile.algalRisk} ·{' '}
            <strong>Stratification:</strong> {profile.stratificationRisk}<br />
            <strong>Priority:</strong> {profile.priority}<br />
            <em className="text-slate-500">Source: {profile.source}</em>
          </div>
        </div>

        {/* ─── RECOMMENDED SETTINGS PANEL ────────────────────────────────── */}
        <div className="rb-card p-5 mb-4 border-2 border-rb-brand/30 bg-gradient-to-br from-rb-brand/5 to-transparent print:break-inside-avoid">
          <div className="flex items-start gap-3 mb-4">
            <Sparkles className="w-6 h-6 text-rb-brand print:text-black shrink-0 mt-0.5" />
            <div className="flex-1">
              <h2 className="text-base font-bold text-slate-100 print:text-black">Recommended Settings — Bed-Disturbance Minimisation</h2>
              <p className="text-[11px] text-slate-400 print:text-slate-700 mt-1">
                Optimised for the <strong>worst-case bed</strong> ({BED_TYPES.find(b => b.id === recommendations.bedId)?.label})
                at the <strong>shallowest deployment depth</strong> ({recommendations.criticalDepthM} m).
                Algorithm sweeps mounting height × airflow × pore size × diffuser count to minimise θ/θ_cr while maintaining
                bubble Ø in the fine-bubble band (1–5 mm) and SOTE ≥ 2.5 %/m.
              </p>
            </div>
          </div>

          {/* Current vs Recommended comparison */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {[
              { lbl: 'Mounting height', curr: spec.mountingHeightM, rec: recommendations.recMountingHeightM, unit: 'm', dp: 2 },
              { lbl: 'Airflow / diffuser', curr: spec.airflowPerDiffNm3h, rec: recommendations.recAirflowPerDiff, unit: 'Nm³/h', dp: 2 },
              { lbl: 'Pore size', curr: spec.poreSizeUm, rec: recommendations.recPoreSizeUm, unit: 'μm', dp: 0 },
              { lbl: 'Diffusers / module', curr: spec.diffuserCount, rec: recommendations.recDiffuserCount, unit: '', dp: 0 },
            ].map(({ lbl, curr, rec, unit, dp }) => {
              const changed = Math.abs(curr - rec) > 0.001;
              return (
                <div key={lbl} className="bg-slate-900/60 print:bg-white border border-slate-700/40 print:border-slate-300 rounded-xl p-3">
                  <div className="text-[10px] uppercase text-slate-500 print:text-slate-600 tracking-wider mb-1">{lbl}</div>
                  <div className="flex items-baseline justify-between mb-0.5">
                    <span className="text-[10px] text-slate-500">Current</span>
                    <span className="font-mono text-xs text-slate-300 print:text-black">{curr.toFixed(dp)} {unit}</span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-[10px] text-rb-brand print:text-slate-700">Recommended</span>
                    <span className={`font-mono text-sm font-bold ${changed ? 'text-rb-brand print:text-black' : 'text-emerald-400 print:text-black'}`}>
                      {rec.toFixed(dp)} {unit}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Expected stability */}
          <div className={`p-3 rounded-xl mb-3 border ${
            recommendations.expectedRatio < 1.0
              ? 'bg-emerald-500/10 border-emerald-500/30 print:bg-white print:border-slate-400'
              : recommendations.expectedRatio < 1.5
              ? 'bg-amber-500/10 border-amber-500/30 print:bg-white print:border-slate-400'
              : 'bg-red-500/10 border-red-500/30 print:bg-white print:border-slate-400'
          }`}>
            <div className="flex items-center gap-2 mb-1">
              {recommendations.expectedRatio < 1.0
                ? <CheckCircle2 className="w-4 h-4 text-emerald-400 print:text-black" />
                : <AlertTriangle className="w-4 h-4 text-amber-400 print:text-black" />}
              <span className="text-[11px] font-bold text-slate-100 print:text-black">
                Expected θ/θ_cr ratio: {recommendations.expectedRatio.toFixed(2)}× —
                {recommendations.expectedRatio < 1.0 ? ' BED STABLE'
                  : recommendations.expectedRatio < 1.5 ? ' MILD RESUSPENSION (acceptable)'
                  : ' ABOVE THRESHOLD (review parameters)'}
              </span>
            </div>
            <p className="text-[10px] text-slate-400 print:text-slate-700 leading-relaxed">
              {recommendations.rationale}
            </p>
          </div>

          {/* Apply button */}
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={applyRecommendations}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-rb-brand text-slate-900 font-bold text-xs hover:bg-rb-brand/90 transition-all shadow-lg shadow-amber-900/20">
              <Zap className="w-4 h-4" /> Apply Recommended Settings to All Pages
            </button>
            <button onClick={() => {
              savePersisted(KEYS.DIFFUSER_MODULE, spec);
              toast.success('Current design saved to persistent storage');
            }}
              className="flex items-center gap-1.5 text-xs px-3 py-2.5 rounded-xl bg-slate-800/60 text-slate-300 border border-slate-700/40 hover:bg-slate-800 transition-all font-semibold">
              <Save className="w-3.5 h-3.5" /> Save Current
            </button>
          </div>
          <p className="text-[9px] text-slate-500 print:text-slate-600 text-center mt-2">
            All settings are stored in localStorage and shared across every page in this session.
            Reservoir Layout, Calculation Steps, Validation Report, and Module Designer all read the same source of truth.
          </p>
        </div>

        {/* ─── BED TYPE SELECTOR ─────────────────────────────────────────── */}
        <div className="rb-card p-4 mb-4 print:break-inside-avoid">
          <h2 className="text-sm font-bold text-slate-100 print:text-black mb-2">Bed Type Selection</h2>
          <p className="text-[11px] text-slate-400 print:text-slate-700 mb-3">
            Three reservoir bed conditions are analysed. Each represents a different state Barcombe Reservoir may
            be in over its dredging cycle. Select a bed below to see how the active aeration design interacts with that substrate.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {BED_TYPES.map(b => (
              <button key={b.id} onClick={() => setSelectedBed(b.id)}
                className={`rounded-xl p-3 text-left border-2 transition-all ${
                  selectedBed === b.id
                    ? 'bg-rb-brand/10 border-rb-brand/40 print:bg-slate-100 print:border-slate-700'
                    : 'border-slate-700/40 hover:border-slate-600 bg-slate-800/20'
                }`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-4 h-4 rounded-full shrink-0" style={{ background: b.colour }} />
                  <span className="text-xs font-bold text-slate-100 print:text-black">{b.label}</span>
                </div>
                <p className="text-[10px] text-slate-400 print:text-slate-700 mb-2">{b.desc}</p>
                <div className="grid grid-cols-2 gap-1 text-[10px] font-mono">
                  <div className="text-slate-500 print:text-slate-600">d₅₀:</div><div className="text-slate-200 print:text-black">{b.d50_um} μm</div>
                  <div className="text-slate-500 print:text-slate-600">ρ_s:</div><div className="text-slate-200 print:text-black">{b.rho_s} kg/m³</div>
                  <div className="text-slate-500 print:text-slate-600">θ_cr:</div><div className="text-slate-200 print:text-black">{b.theta_cr}</div>
                  <div className="text-slate-500 print:text-slate-600">cohesion k:</div><div className="text-slate-200 print:text-black">×{b.k_cohesive}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* ─── DIFFUSER TYPE & PLUME SHAPE BANNER ──────────────────────── */}
        {(() => {
          const dSpec = specFromLegacy(spec);
          return (
            <div className="rb-card p-3 mb-4 border border-cyan-500/30 bg-cyan-500/5 text-[11px] print:bg-white print:border-slate-300">
              <div className="flex items-start gap-2.5">
                <Layers className="w-4 h-4 text-cyan-400 print:text-black shrink-0 mt-0.5" />
                <div className="text-slate-300 print:text-black">
                  <div className="font-bold text-cyan-300 print:text-black mb-1">
                    Active diffuser geometry: <span className="font-mono">{describeShape(dSpec)}</span>
                  </div>
                  <div className="text-[10px] text-slate-400 print:text-slate-700 mb-1">
                    Plume model: <strong>{PLUME_SHAPE_LABEL[dSpec.shape]}</strong>
                  </div>
                  <div className="text-[10px] text-slate-500 print:text-slate-700">
                    {dSpec.shape === 'tube' ? (
                      <>
                        <strong>Tube physics:</strong> Buoyancy flux divided by total tube length L_total.
                        Plume forms a planar curtain instead of a cone. Bed footprint per module ≈ (L+2r)×2r rectangular,
                        typically 30-60% greater coverage than disc array of same airflow but with{' '}
                        <strong className="text-emerald-400 print:text-black">lower bed shear per point</strong> — preferred for cohesive silt beds.
                      </>
                    ) : (
                      <>
                        <strong>Disc/dome physics:</strong> Each diffuser is a point source generating an
                        axisymmetric cone. Plume centreline velocity scales as (B₀·H)^(1/3) per Seol (2007).
                        Higher bed shear directly beneath module — best for non-cohesive (sandy) beds.
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ─── PHYSICS MATRIX ────────────────────────────────────────────── */}
        <div className="rb-card p-4 mb-4 print:break-before-page">
          <h2 className="text-sm font-bold text-slate-100 print:text-black mb-2 flex items-center gap-2">
            <Layers className="w-4 h-4 text-rb-brand print:text-black" />
            Plume → Bed Interaction Matrix · {bed.label}
          </h2>
          <p className="text-[11px] text-slate-400 print:text-slate-700 mb-3">
            For each depth, the bubble plume induces a bed velocity which creates shear stress. The Shields parameter compares
            this shear to what the bed can resist. <strong className="text-rb-brand print:text-black">θ &lt; θ_cr</strong> = stable;
            <strong className="text-amber-400 print:text-black"> 1 &lt; θ/θ_cr &lt; 2</strong> = mild resuspension;
            <strong className="text-red-400 print:text-black"> &gt; 2</strong> = significant erosion.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full text-[10px] border-collapse">
              <thead>
                <tr className="bg-slate-800/60 print:bg-slate-200 text-slate-300 print:text-slate-700">
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-left">Depth</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">H_eff (m)</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">d_b bed (mm)</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">d_b surf (mm)</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">v_rise (m/s)</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">SOTE %/m</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">U_bed (m/s)</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">τ_bed (Pa)</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">θ</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">θ/θ_cr_eff</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">f_resusp</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">Erosion (kg/m²/h)</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {DEPTHS.map(d => {
                  const p = matrix[bed.id][d];
                  return (
                    <tr key={d}
                      onClick={() => setSelectedDepth(d)}
                      className={`cursor-pointer ${selectedDepth === d ? 'bg-rb-brand/10 print:bg-slate-100' : 'hover:bg-slate-800/30 print:hover:bg-transparent'}`}>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 font-bold">{d.toFixed(1)} m</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(p.H_eff, 2)}</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(p.d_b_bed, 2)}</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(p.d_b_surf, 2)}</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(p.v_rise, 3)}</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(p.soteM, 2)}</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(p.U_plume_bed, 3)}</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(p.tau_bed, 3)}</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmtSci(p.theta)}</td>
                      <td className={`p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono font-bold ${
                        p.theta / p.theta_cr_eff < 1 ? 'text-emerald-400 print:text-black'
                        : p.theta / p.theta_cr_eff < 2 ? 'text-amber-400 print:text-black' : 'text-red-400 print:text-black'
                      }`}>{fmt(p.theta / p.theta_cr_eff, 2)}</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(p.resusp_factor, 2)}×</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(p.erosion_rate, 3)}</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400">
                        {p.stable
                          ? <span className="text-emerald-400 print:text-black font-bold">✓ STABLE</span>
                          : p.theta / p.theta_cr_eff < 2
                          ? <span className="text-amber-400 print:text-black font-bold">⚠ MILD RESUSP</span>
                          : <span className="text-red-400 print:text-black font-bold">✗ ERODING</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-slate-500 print:text-slate-700 mt-2 italic">
            Click any row to view detailed interpretation below. Currently selected: <strong>{selectedDepth} m</strong>.
          </p>
        </div>

        {/* ─── DETAIL INTERPRETATION FOR SELECTED DEPTH ──────────────────── */}
        <div className="rb-card p-4 mb-4 print:break-inside-avoid">
          <h3 className="text-sm font-bold text-slate-100 print:text-black mb-3 flex items-center gap-2">
            <Info className="w-4 h-4 text-rb-brand print:text-black" />
            Detailed Interpretation — {bed.label} at {selectedDepth} m
          </h3>
          {(() => {
            const p = matrix[bed.id][selectedDepth];
            const ratio = p.theta / p.theta_cr_eff;
            return (
              <div className="space-y-2">
                <div className="text-[11px] text-slate-300 print:text-slate-700 leading-relaxed">
                  {p.stable ? (
                    <p>
                      <strong className="text-emerald-400 print:text-black">The bed is stable.</strong>{' '}
                      Plume-induced velocity ({fmt(p.U_plume_bed, 3)} m/s) generates {fmt(p.tau_bed, 3)} Pa of bed shear —
                      below the {fmt(p.theta_cr_eff, 3)} effective critical Shields threshold for {bed.label.toLowerCase()}.
                      No resuspension of the existing bed occurs. Oxygen transfer occurs by diffusion only, with
                      penetration depth of ~{fmt(p.o2_penetration, 1)} mm.
                    </p>
                  ) : ratio < 2 ? (
                    <p>
                      <strong className="text-amber-400 print:text-black">Mild resuspension occurs.</strong>{' '}
                      Bed shear stress ({fmt(p.tau_bed, 3)} Pa, θ = {fmtSci(p.theta)}) exceeds the critical Shields parameter
                      by {fmt(ratio, 2)}×. Surface flocs and unconsolidated material are gently mobilised,
                      enhancing SOD by {fmt(p.resusp_factor, 2)}× through increased particle surface area. Erosion
                      rate of {fmt(p.erosion_rate, 3)} kg/m²/h means {fmt(p.erosion_rate * 24, 1)} kg/m²/day of
                      sediment is suspended into the water column at the module footprint.
                    </p>
                  ) : (
                    <p>
                      <strong className="text-red-400 print:text-black">Significant erosion.</strong>{' '}
                      Bed shear ({fmt(p.tau_bed, 3)} Pa) is {fmt(ratio, 1)}× critical — beyond mild surface
                      mobilisation. The full top sediment layer is actively eroded at {fmt(p.erosion_rate, 3)} kg/m²/h
                      ({fmt(p.erosion_rate * 24, 1)} kg/m²/day). Turbidity at the bubble plume base will be high.
                      Mounting height may need to be raised to {fmt(spec.mountingHeightM + 0.2, 2)} m to reduce
                      direct bed impingement.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2 mt-3">
                  <div className="rb-section p-2 print:border print:border-slate-300">
                    <div className="text-[9px] text-slate-500 print:text-slate-600 uppercase">Bubble at orifice</div>
                    <div className="text-base font-bold font-mono text-rb-brand print:text-black">{fmt(p.d_b_bed, 2)} mm</div>
                    <div className="text-[9px] text-slate-600 print:text-slate-700">Fritz (1935) + Loubière (2003)</div>
                  </div>
                  <div className="rb-section p-2 print:border print:border-slate-300">
                    <div className="text-[9px] text-slate-500 print:text-slate-600 uppercase">Bubble at surface</div>
                    <div className="text-base font-bold font-mono text-rb-brand print:text-black">{fmt(p.d_b_surf, 2)} mm</div>
                    <div className="text-[9px] text-slate-600 print:text-slate-700">Ideal gas expansion (P_bed/P_atm)^(1/3)</div>
                  </div>
                  <div className="rb-section p-2 print:border print:border-slate-300">
                    <div className="text-[9px] text-slate-500 print:text-slate-600 uppercase">O₂ penetration into bed</div>
                    <div className="text-base font-bold font-mono text-cyan-400 print:text-black">{fmt(p.o2_penetration, 1)} mm</div>
                    <div className="text-[9px] text-slate-600 print:text-slate-700">Rasmussen &amp; Jørgensen (1992)</div>
                  </div>
                  <div className="rb-section p-2 print:border print:border-slate-300">
                    <div className="text-[9px] text-slate-500 print:text-slate-600 uppercase">SOD enhancement</div>
                    <div className="text-base font-bold font-mono text-amber-400 print:text-black">{fmt(p.sod_enhance, 2)}×</div>
                    <div className="text-[9px] text-slate-600 print:text-slate-700">vs undisturbed baseline</div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* ─── EXPANDABLE SCIENCE SECTIONS ───────────────────────────────── */}
        <div className="rb-card p-4 mb-4 print:break-before-page">
          <h2 className="text-sm font-bold text-slate-100 print:text-black mb-3 flex items-center gap-2">
            <Info className="w-4 h-4 text-rb-brand print:text-black" />
            Science Background — Click to Expand
          </h2>

          <Expandable title="0. Tube vs disc diffuser — line-source vs point-source physics" defaultOpen>
            <p>
              The aeration system uses one of two fundamental diffuser geometries, each with distinct plume physics:
            </p>
            <ul className="list-disc list-inside ml-3 space-y-1 my-2">
              <li>
                <strong>Disc / dome (point source):</strong> Each diffuser is treated as an isolated buoyant source.
                Plume forms an <strong>axisymmetric cone</strong> with apex at the diffuser. Centreline velocity at
                depth H is U = 1.46 × (B₀ × H)^(1/3) per Seol et al. (2007). Coverage area is circular (π × r²) per
                diffuser. Suitable for sandy / firm beds where focused mixing is desirable.
              </li>
              <li>
                <strong>Tube / strip (line source):</strong> Each tube acts as a continuous line of bubbles.
                Plume forms a <strong>planar curtain</strong> perpendicular to the tube axis. Buoyancy flux per
                unit length B₀_line = B₀_total / L. Centreline velocity follows U = c × (B₀_line)^(1/3)
                per Wüest et al. (1992) and McGinnis & Little (2002). Coverage is rectangular (L + 2r) × 2r per tube.
                Bed shear is <strong>distributed along the tube</strong> rather than concentrated under a point —
                strongly preferred for cohesive silt where peak shear must stay below Shields criterion.
              </li>
            </ul>
            <p>
              <strong>Practical impact:</strong> For the same module airflow, a tube layout delivers ~30–60% more
              bed coverage area than a disc layout but with proportionally lower peak bed velocity. This is why
              tube diffusers are the default recommendation for pre-dredge silt management.
            </p>
            <p>
              <strong>Selected geometry:</strong> {describeShape(specFromLegacy(spec))}.<br/>
              <strong>Plume model in use:</strong> {PLUME_SHAPE_LABEL[specFromLegacy(spec).shape]}.
            </p>
            <p className="text-[10px] text-slate-500 mt-2">
              References: Wüest, Brooks &amp; Imboden (1992) Water Resour. Res. 28(12); McGinnis &amp; Little (2002)
              Water Res. 36; Asaeda &amp; Imberger (1993) J. Fluid Mech. 249; Brevis &amp; García (2011) J. Hydro-environ. Res. 5(1).
            </p>
          </Expandable>

          <Expandable title="1. Bubble formation physics (Fritz 1935 + Loubière & Hébrard 2003)" defaultOpen>
            <p>
              When air is forced through a porous diffuser, bubbles form at each pore by competition between buoyancy
              (lifting the bubble) and surface tension (anchoring it). At low flow, Fritz (1935) gives the static balance:
            </p>
            <div className="my-2 px-3 py-1.5 rounded font-mono bg-slate-900/60 print:bg-slate-100 text-[10px]">
              d_static = [6·σ·d_pore / (ρ_w·g)]^(1/3)
            </div>
            <p>
              At higher flow rates, dynamic detachment dominates. Loubière &amp; Hébrard (2003) showed that the
              flow per pore (q_p) inflates the bubble before it detaches:
            </p>
            <div className="my-2 px-3 py-1.5 rounded font-mono bg-slate-900/60 print:bg-slate-100 text-[10px]">
              d_bubble = d_static × (1 + 0.9·(q_pore·10⁹)^0.25)
            </div>
            <p>
              For your session: <strong>pore size = {spec.poreSizeUm} μm</strong>, airflow per pore =
              <strong> {fmt(spec.airflowPerDiffNm3h * 1e6 / 3600 / diffuserPores(spec.diffuserType, spec.diffuserDiamMm), 2)} μL/s</strong>{' '}
              → operating bubble diameter <strong>{fmt(matrix[bed.id][selectedDepth].d_b_bed, 2)} mm</strong>.
              The fine-bubble regime (1–5 mm) maximises mass-transfer surface area while keeping rise velocity moderate.
            </p>
          </Expandable>

          <Expandable title="2. Bubble plume scaling (Seol 2007 / Schladow 1993)">
            <p>
              Each module emits a buoyant bubble plume. The total buoyancy flux is:
            </p>
            <div className="my-2 px-3 py-1.5 rounded font-mono bg-slate-900/60 print:bg-slate-100 text-[10px]">
              B₀ = g · Q_actual  [m⁴/s³]
            </div>
            <p>
              The plume entrains surrounding water and accelerates. By dimensional analysis (Morton 1956, refined
              by Seol 2007 with PIV measurements), the centreline velocity at depth H is:
            </p>
            <div className="my-2 px-3 py-1.5 rounded font-mono bg-slate-900/60 print:bg-slate-100 text-[10px]">
              U_plume = 1.46 × (B₀ · H)^(1/3)
            </div>
            <p>
              At the bed itself, the plume base creates an impingement zone with velocity ~U_plume reducing to
              zero at the wall. For shear stress estimation we use the plume velocity multiplied by a bed-drag
              coefficient C_D = 0.01 (van Rijn 1984):
            </p>
            <div className="my-2 px-3 py-1.5 rounded font-mono bg-slate-900/60 print:bg-slate-100 text-[10px]">
              τ_bed = ρ_w · C_D · U_plume²
            </div>
            <p>
              For your session at <strong>{selectedDepth} m depth</strong>: U_bed =
              <strong> {fmt(matrix[bed.id][selectedDepth].U_plume_bed, 3)} m/s</strong>,
              τ_bed = <strong>{fmt(matrix[bed.id][selectedDepth].tau_bed, 3)} Pa</strong>.
            </p>
          </Expandable>

          <Expandable title="3. Shields criterion for sediment entrainment (Shields 1936)">
            <p>
              Whether a particle on the bed is moved by flow depends on the dimensionless Shields parameter:
            </p>
            <div className="my-2 px-3 py-1.5 rounded font-mono bg-slate-900/60 print:bg-slate-100 text-[10px]">
              θ = τ_bed / [(ρ_s − ρ_w) · g · d₅₀]
            </div>
            <p>
              For non-cohesive sediment (sand), the threshold is empirically <strong>θ_cr ≈ 0.045–0.06</strong>{' '}
              (van Rijn 1984). For cohesive sediment (clay, silt with organic content), inter-particle forces
              raise the effective threshold by a factor of 2–5× depending on water content, age, and organic
              binders (Mehta 1986). We apply a <strong>k_cohesive multiplier</strong>:
            </p>
            <div className="my-2 px-3 py-1.5 rounded font-mono bg-slate-900/60 print:bg-slate-100 text-[10px]">
              θ_cr_effective = θ_cr × k_cohesive
            </div>
            <p>
              For <strong>{bed.label}</strong>: d₅₀ = {bed.d50_um} μm, ρ_s = {bed.rho_s} kg/m³,
              θ_cr = {bed.theta_cr}, k_cohesive = ×{bed.k_cohesive} → θ_cr_eff = <strong>{fmt(bed.theta_cr * bed.k_cohesive, 3)}</strong>.
              The bed is mobilised when θ exceeds θ_cr_eff.
            </p>
          </Expandable>

          <Expandable title="4. Cohesive erosion law (Partheniades 1965 / Mehta 1986)">
            <p>
              When the bed is exceeded (θ &gt; θ_cr), particles enter suspension. For cohesive sediments,
              Partheniades (1965) established the linear erosion law:
            </p>
            <div className="my-2 px-3 py-1.5 rounded font-mono bg-slate-900/60 print:bg-slate-100 text-[10px]">
              E = E₀ · (θ/θ_cr − 1)   [kg/m²/s]
            </div>
            <p>
              Where E₀ ≈ 1×10⁻⁴ kg/m²/s for typical reservoir silts. Mehta (1986) extended this with a
              resuspension factor that quantifies how much background SOD increases when the bed is mildly
              disturbed (without full erosion):
            </p>
            <div className="my-2 px-3 py-1.5 rounded font-mono bg-slate-900/60 print:bg-slate-100 text-[10px]">
              f_resusp = max(1, 1 + 0.5 · (θ/θ_cr − 1)),  capped at 3.0
            </div>
            <p>
              In your design, at the selected depth {selectedDepth} m with {bed.label.toLowerCase()}, the
              erosion rate is <strong>{fmt(matrix[bed.id][selectedDepth].erosion_rate, 3)} kg/m²/h</strong>
              {' '}beneath each module's plume base.
            </p>
          </Expandable>

          <Expandable title="5. SOD interaction with aeration (Hickey & Gibbs 2009)">
            <p>
              Baseline sediment oxygen demand (SOD) is driven by aerobic decomposition of organic matter near
              the bed surface. Hickey &amp; Gibbs (2009) report 0.5–3.0 g O₂/m²/d for typical UK reservoir
              sediments, with the upper end for pre-dredge eutrophic conditions.
            </p>
            <p>
              When the bed is gently disturbed by bubble plumes, two competing effects emerge:
            </p>
            <ul className="list-disc list-inside ml-3 space-y-1">
              <li><strong>Negative:</strong> Resuspended organic floc has higher surface area in contact with O₂ → instantaneous SOD increases by factor f_resusp.</li>
              <li><strong>Positive:</strong> O₂ penetration depth into the consolidated bed increases with surface mixing → long-term, the anoxic black layer recedes, reducing phosphorus release.</li>
            </ul>
            <p className="mt-2">
              Net effect for Barcombe pre-dredge: aeration is essential to switch the sediment surface from
              anaerobic (P-releasing) to aerobic (P-binding) within ~2–4 weeks of operation. The transient
              SOD spike is acceptable cost.
            </p>
          </Expandable>

          <Expandable title="6. O₂ penetration depth into sediment (Rasmussen & Jørgensen 1992)">
            <p>
              The depth to which dissolved oxygen penetrates the consolidated sediment is governed by molecular
              diffusion vs. consumption:
            </p>
            <div className="my-2 px-3 py-1.5 rounded font-mono bg-slate-900/60 print:bg-slate-100 text-[10px]">
              δ_O₂ ≈ √(2 · D_O₂ · C_water / SOD_rate)
            </div>
            <p>
              With D_O₂ ≈ 1×10⁻⁹ m²/s, C_water = 4–8 mg/L (post-aeration), SOD = 1–3 g O₂/m²/d, typical penetration is:
            </p>
            <ul className="list-disc list-inside ml-3 space-y-0.5">
              <li><strong>Fine silt / clay:</strong> 1–2 mm (low porosity, high O₂ consumption per m²)</li>
              <li><strong>Medium silt:</strong> 3–5 mm</li>
              <li><strong>Fine sand:</strong> 6–10 mm (high porosity, lower organic content)</li>
            </ul>
            <p className="mt-2">
              For <strong>{bed.label}</strong>: estimated <strong>{fmt(matrix[bed.id][selectedDepth].o2_penetration, 1)} mm</strong>{' '}
              O₂ penetration. This is the depth within which iron and manganese stay oxidised (preventing P release),
              and where aerobic bacteria dominate over the methanogens/sulphate-reducers below.
            </p>
          </Expandable>
        </div>

        {/* ─── BED COMPARISON SUMMARY ────────────────────────────────────── */}
        <div className="rb-card p-4 mb-4 print:break-before-page">
          <h2 className="text-sm font-bold text-slate-100 print:text-black mb-3">
            Cross-Bed Comparison at {selectedDepth} m
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-[10px] border-collapse">
              <thead>
                <tr className="bg-slate-800/60 print:bg-slate-200 text-slate-300 print:text-slate-700">
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-left">Bed type</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">d₅₀</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">θ_cr_eff</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">θ</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">θ/θ_cr</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">f_resusp</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">Erosion (kg/m²/d)</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">O₂ pen. (mm)</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-left">Outcome</th>
                </tr>
              </thead>
              <tbody>
                {BED_TYPES.map(b => {
                  const p = matrix[b.id][selectedDepth];
                  const ratio = p.theta / p.theta_cr_eff;
                  return (
                    <tr key={b.id}>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400">
                        <div className="flex items-center gap-1.5">
                          <div className="w-3 h-3 rounded-full shrink-0" style={{ background: b.colour }} />
                          <strong>{b.label}</strong>
                        </div>
                      </td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{b.d50_um} μm</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(p.theta_cr_eff, 3)}</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmtSci(p.theta)}</td>
                      <td className={`p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono font-bold ${
                        ratio < 1 ? 'text-emerald-400 print:text-black' : ratio < 2 ? 'text-amber-400 print:text-black' : 'text-red-400 print:text-black'
                      }`}>{fmt(ratio, 2)}</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(p.resusp_factor, 2)}×</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(p.erosion_rate * 24, 2)}</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(p.o2_penetration, 1)}</td>
                      <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-[10px]">
                        {p.stable ? <span className="text-emerald-400 print:text-black">Stable, diffusion only</span>
                          : ratio < 2 ? <span className="text-amber-400 print:text-black">Mild resuspension</span>
                          : <span className="text-red-400 print:text-black">Active erosion</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-slate-500 print:text-slate-700 mt-2 italic">
            Note: <strong>{bed.note}</strong>
          </p>
        </div>

        {/* ─── ARRAY LAYOUT ON RESERVOIR ─────────────────────────────────── */}
        <div className="rb-card p-4 mb-4 print:break-before-page">
          <h2 className="text-sm font-bold text-slate-100 print:text-black mb-2 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-rb-brand print:text-black" />
            Array Layout on Reservoir Bed
          </h2>
          <p className="text-[11px] text-slate-400 print:text-slate-700 mb-3">
            The {totals.arrays} arrays (containing <strong>{totals.modules} modules</strong> = <strong>{totals.diffusers} diffusers</strong>)
            are positioned across the depth zones below. Each array (A–H) has its own sub-manifold and is placed on the
            deepest available substrate to maximise SOTE while remaining within structural deployment depth.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
            <ReservoirLayoutSVG placements={placements} bed={bed} />

            <div className="space-y-2">
              <h3 className="text-xs font-bold text-slate-200 print:text-black mb-1.5">Array directory</h3>
              {placements.map(p => (
                <div key={p.zone.id} className="rb-section p-2.5 print:border print:border-slate-300">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black bg-rb-brand text-slate-900 shrink-0">
                      {p.zone.id}
                    </span>
                    <span className="text-[11px] font-semibold text-slate-100 print:text-black truncate">{p.zone.label}</span>
                  </div>
                  <div className="text-[10px] text-slate-400 print:text-slate-700 grid grid-cols-2 gap-x-2 gap-y-0.5 ml-8">
                    <div><span className="text-slate-500">Depth:</span> <strong className="font-mono">{p.zone.depthMid} m</strong></div>
                    <div><span className="text-slate-500">Modules:</span> <strong className="font-mono">{p.moduleCount}</strong></div>
                    <div><span className="text-slate-500">Diff:</span> <strong className="font-mono">{p.moduleCount * spec.diffuserCount}</strong></div>
                    <div><span className="text-slate-500">Spacing:</span> <strong className="font-mono">{fmt(p.spacing, 1)} m</strong></div>
                  </div>
                </div>
              ))}
              <div className="rb-section p-2.5 bg-emerald-500/5 border-emerald-500/30 print:bg-slate-100 print:border-slate-400 mt-3">
                <div className="text-[10px] uppercase text-slate-500 print:text-slate-600 mb-1">Totals</div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div><div className="text-base font-bold text-rb-brand print:text-black">{totals.arrays}</div><div className="text-[9px] text-slate-500">arrays</div></div>
                  <div><div className="text-base font-bold text-emerald-400 print:text-black">{totals.modules}</div><div className="text-[9px] text-slate-500">modules</div></div>
                  <div><div className="text-base font-bold text-cyan-400 print:text-black">{totals.diffusers}</div><div className="text-[9px] text-slate-500">diffusers</div></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ─── PER-ARRAY POSITION TABLE ──────────────────────────────────── */}
        <div className="rb-card p-4 mb-4 print:break-inside-avoid">
          <h2 className="text-sm font-bold text-slate-100 print:text-black mb-2">
            Array Positions (Survey Coordinates)
          </h2>
          <p className="text-[11px] text-slate-400 print:text-slate-700 mb-2">
            Positions referenced to the reservoir SW corner in metres. Each array's centroid is given; individual module positions
            (relative offsets from centroid) are shown below.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-[10px] border-collapse">
              <thead>
                <tr className="bg-slate-800/60 print:bg-slate-200 text-slate-300 print:text-slate-700">
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-left">Array</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-left">Zone</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">Centroid X (m)</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">Centroid Y (m)</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">Depth (m)</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">Modules</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">Diffusers</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right">Spacing</th>
                  <th className="p-1.5 border border-slate-700/40 print:border-slate-400 text-left">Module offsets (Δx, Δy m from centroid)</th>
                </tr>
              </thead>
              <tbody>
                {placements.map(p => (
                  <tr key={p.zone.id}>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 font-bold text-rb-brand print:text-black">{p.zone.id}</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400">{p.zone.label}</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{p.zone.cx}</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{p.zone.cy}</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{p.zone.depthMid}</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono font-bold">{p.moduleCount}</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono font-bold text-emerald-400 print:text-black">{p.moduleCount * spec.diffuserCount}</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-right font-mono">{fmt(p.spacing, 1)} m</td>
                    <td className="p-1.5 border border-slate-700/40 print:border-slate-400 text-[9px] font-mono text-slate-400 print:text-slate-700">
                      {p.modules.slice(0, 6).map((m, i) =>
                        `(${(m.x - p.zone.cx).toFixed(0)},${(m.y - p.zone.cy).toFixed(0)})`
                      ).join(' · ')}
                      {p.modules.length > 6 ? ` … +${p.modules.length - 6} more` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ─── REFERENCES ───────────────────────────────────────────────── */}
        <div className="rb-card p-4 mb-4 print:break-inside-avoid">
          <h2 className="text-sm font-bold text-slate-100 print:text-black mb-2">References</h2>
          <ol className="list-decimal list-inside text-[10px] text-slate-400 print:text-slate-700 space-y-0.5 leading-relaxed">
            <li>Fritz, W. (1935). Berechnung des Maximalvolumens von Dampfblasen.</li>
            <li>Shields, A. (1936). Anwendung der Ähnlichkeitsmechanik auf die Geschiebebewegung.</li>
            <li>Partheniades, E. (1965). Erosion and deposition of cohesive soils. J. Hydraul. Div. 91(1), 105–139.</li>
            <li>Mehta, A.J. (1986). Characterization of cohesive sediment properties and transport processes in estuaries.</li>
            <li>van Rijn, L.C. (1984). Sediment transport, part I: bed load transport. J. Hydraul. Eng. 110(10), 1431–1456.</li>
            <li>Rasmussen, H. &amp; Jørgensen, B.B. (1992). Microelectrode studies of seasonal oxygen uptake in a coastal sediment. Mar. Ecol. Prog. Ser. 81, 289–303.</li>
            <li>Schladow, S.G. (1993). Lake destratification by bubble-plume systems. J. Hydraul. Eng. 119(3), 350–368.</li>
            <li>Loubière, K. &amp; Hébrard, G. (2003). Bubble formation at a rigid or flexible orifice. Chem. Eng. Process. 42(11), 875–886.</li>
            <li>Seol, D.G. et al. (2007). PIV measurements of bubble plume flow. J. Hydraul. Eng. 133(6), 627–640.</li>
            <li>Hickey, C.W. &amp; Gibbs, M.M. (2009). Lake sediment oxygen demand and nutrient dynamics. N.Z. J. Mar. Freshwater Res. 43(2), 693–714.</li>
          </ol>
        </div>

        <div className="text-center text-[10px] text-slate-600 print:text-slate-500 pt-2 pb-8 print:pb-0">
          Reynolds &amp; Bauhm Smart Water · Sediment Bed Physics · {new Date().toLocaleString('en-GB')}
        </div>
      </div>
    </div>
  );
}

// ─── Reservoir Layout SVG with array positions ────────────────────────────────
function ReservoirLayoutSVG({ placements, bed }: { placements: ArrayPlacement[]; bed: BedType }) {
  const VIEW_W = 220, VIEW_H = 480;
  const polyPts = RESERVOIR_POLY.map(p => p.join(',')).join(' ');

  return (
    <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      className="w-full border border-slate-700/50 print:border-slate-400 rounded-xl print:rounded-none"
      style={{ background: '#0d1420', maxHeight: '70vh' }}>

      <defs>
        <clipPath id="bedphys-clip"><polygon points={polyPts} /></clipPath>
      </defs>

      {/* Reservoir base (bed-type colour) */}
      <polygon points={polyPts} fill={bed.colour} opacity="0.55" />

      {/* Deep zone overlays (visualise depth) */}
      <g clipPath="url(#bedphys-clip)">
        <ellipse cx={92}  cy={88}  rx={38} ry={52} fill="#2a6b4e" opacity="0.5" />
        <ellipse cx={62}  cy={195} rx={20} ry={35} fill="#2a6b4e" opacity="0.5" />
        <ellipse cx={100} cy={278} rx={22} ry={28} fill="#2a6b4e" opacity="0.5" />
        <ellipse cx={152} cy={170} rx={14} ry={22} fill="#2a6b4e" opacity="0.5" />
        <ellipse cx={150} cy={90}  rx={19} ry={26} fill="#2a4488" opacity="0.7" />
      </g>

      {/* Outline */}
      <polygon points={polyPts} fill="none" stroke="#d2823a" strokeWidth="1.2" />

      {/* Array zones (showing extent) */}
      {placements.map(p => (
        <g key={`zone-${p.zone.id}`}>
          <ellipse cx={p.zone.cx} cy={p.zone.cy} rx={p.zone.rx} ry={p.zone.ry}
            fill="none" stroke="#cda42e" strokeWidth="0.4" strokeDasharray="2,2" opacity="0.6" />
        </g>
      ))}

      {/* Modules (small squares) */}
      {placements.map(p => p.modules.map((m, i) => (
        <rect key={`${p.zone.id}-${i}`}
          x={m.x - 1.2} y={m.y - 1.2} width={2.4} height={2.4}
          fill="#cda42e" stroke="#1a1a1a" strokeWidth="0.15" />
      )))}

      {/* Array labels (large letters at centroid) */}
      {placements.map(p => (
        <g key={`lbl-${p.zone.id}`}>
          <circle cx={p.zone.cx} cy={p.zone.cy} r={4.5} fill="#0d1420" fillOpacity="0.85" stroke="#cda42e" strokeWidth="0.6" />
          <text x={p.zone.cx} y={p.zone.cy + 1.5} textAnchor="middle" fontSize="5" fontWeight="bold" fill="#cda42e">
            {p.zone.id}
          </text>
        </g>
      ))}

      {/* Scale + North arrow */}
      <g transform="translate(10,460)">
        <line x1={0} y1={0} x2={50} y2={0} stroke="#94a3b8" strokeWidth="1.2" />
        <line x1={0} y1={-2.5} x2={0} y2={2.5} stroke="#94a3b8" strokeWidth="1.2" />
        <line x1={50} y1={-2.5} x2={50} y2={2.5} stroke="#94a3b8" strokeWidth="1.2" />
        <text x={25} y={-4} textAnchor="middle" fontSize="4.5" fill="#94a3b8">50 m</text>
      </g>
      <g transform="translate(197,20)">
        <polygon points="0,-8 -3,4 0,2 3,4" fill="#94a3b8" />
        <text x={0} y={-11} textAnchor="middle" fontSize="4.5" fill="#94a3b8" fontWeight="bold">N</text>
      </g>

      {/* Legend */}
      <g transform="translate(8,20)">
        <rect x={-2} y={-2} width={64} height={28} rx={2} fill="#0d1420" fillOpacity="0.85" />
        <rect width={5} height={5} fill={bed.colour} opacity="0.55" />
        <text x={7} y={4} fontSize="4" fill="#94a3b8">Bed: {bed.label}</text>
        <g transform="translate(0,7)">
          <rect width={5} height={5} fill="#cda42e" />
          <text x={7} y={4} fontSize="4" fill="#94a3b8">Module</text>
        </g>
        <g transform="translate(0,14)">
          <circle cx={2.5} cy={2.5} r={2.5} fill="#0d1420" stroke="#cda42e" strokeWidth="0.5" />
          <text x={7} y={4} fontSize="4" fill="#94a3b8">Array centre</text>
        </g>
        <g transform="translate(0,21)">
          <line x1={0} y1={2.5} x2={5} y2={2.5} stroke="#cda42e" strokeWidth="0.5" strokeDasharray="1,1" />
          <text x={7} y={4} fontSize="4" fill="#94a3b8">Array zone</text>
        </g>
      </g>
    </svg>
  );
}
